import { useEffect, useState } from "react";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Package, Truck, IndianRupee, Loader2, ShieldCheck, Clock, CheckCircle2,
  Plus, ChevronDown, ChevronUp, Zap, BarChart3, RotateCcw, Download,
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";

const B2B_STAGES = [
  { key: "pickup_assigned", label: "Pickup", short: "PU" },
  { key: "picked_up", label: "Collected", short: "CL" },
  { key: "packing", label: "Packing", short: "PK" },
  { key: "at_origin_hub", label: "Origin Hub", short: "OH" },
  { key: "in_linehaul", label: "Line-Haul", short: "LH" },
  { key: "at_dest_hub", label: "Dest Hub", short: "DH" },
  { key: "out_for_delivery", label: "Delivery", short: "DL" },
  { key: "delivered", label: "Done", short: "DN" },
];

function B2BPipeline({ status }) {
  const currentIdx = B2B_STAGES.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-0.5">
      {B2B_STAGES.map((stage, i) => {
        const isDone = i <= currentIdx && currentIdx >= 0;
        const isCurrent = stage.key === status;
        // Extracted from nested ternary (#8 code review).
        let stageClass = "bg-slate-200 text-slate-400";
        if (isDone && isCurrent) stageClass = "bg-[#FF6B00] text-white ring-2 ring-orange-200";
        else if (isDone) stageClass = "bg-emerald-500 text-white";
        return (
          <div key={stage.key} className="flex items-center" title={stage.label}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold ${stageClass}`}>
              {stage.short}
            </div>
            {i < B2B_STAGES.length - 1 && <div className={`w-2 h-0.5 ${isDone && i < currentIdx ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

const REVERSE_STAGES = [
  { key: "return_requested", label: "Requested", short: "RQ" },
  { key: "return_pickup_assigned", label: "Pickup", short: "PU" },
  { key: "return_picked_up", label: "Collected", short: "CL" },
  { key: "return_at_origin_hub", label: "Origin Hub", short: "OH" },
  { key: "return_in_linehaul", label: "Line-Haul", short: "LH" },
  { key: "return_at_dest_hub", label: "Dest Hub", short: "DH" },
  { key: "return_delivered", label: "Returned", short: "RT" },
];

const RETURN_REASONS = ["Damaged", "Wrong item", "Quality issue", "Customer rejected", "Recall", "Other"];

function ReversePipeline({ status }) {
  const currentIdx = REVERSE_STAGES.findIndex(s => s.key === status);
  return (
    <div className="flex items-center gap-0.5">
      {REVERSE_STAGES.map((stage, i) => {
        const isDone = i <= currentIdx && currentIdx >= 0;
        const isCurrent = stage.key === status;
        // Extracted from nested ternary (#8 code review).
        let stageClass = "bg-slate-200 text-slate-400";
        if (isDone && isCurrent) stageClass = "bg-red-500 text-white ring-2 ring-red-200";
        else if (isDone) stageClass = "bg-amber-500 text-white";
        return (
          <div key={stage.key} className="flex items-center" title={stage.label}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold ${stageClass}`}>
              {stage.short}
            </div>
            {i < REVERSE_STAGES.length - 1 && <div className={`w-2 h-0.5 ${isDone && i < currentIdx ? "bg-amber-400" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

export default function B2BDashboard() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBook, setShowBook] = useState(false);
  const [booking, setBooking] = useState(false);
  const [pricePreview, setPricePreview] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");
  const [returnDialog, setReturnDialog] = useState(null); // tracking_id to return
  const [returnReason, setReturnReason] = useState("");
  const [returnNotes, setReturnNotes] = useState("");
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  const [form, setForm] = useState({
    pickup_address: "", pickup_city: "", delivery_address: "", delivery_city: "",
    weight_kg: 3, package_description: "", declared_value: 0, payment_method: "online",
    notes: "", addons: [], item_count: 1,
  });

  const fetchData = async () => {
    try {
      const [sRes, oRes, rRes] = await Promise.all([
        api.get("/b2b/my-stats"),
        api.get("/b2b/my-orders"),
        api.get("/b2b/my-returns"),
      ]);
      setStats(sRes.data);
      setOrders(oRes.data);
      setReturns(rRes.data);
    } catch (err) {
      console.error("B2BDashboard: failed to fetch data", err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const toggleAddon = (key) => {
    setForm(prev => ({
      ...prev,
      addons: prev.addons.includes(key)
        ? prev.addons.filter(a => a !== key)
        : [...prev.addons, key]
    }));
    setPricePreview(null);
  };

  const calculatePrice = async () => {
    if (!form.pickup_city || !form.delivery_city) {
      toast.error("Enter pickup & delivery cities");
      return;
    }
    setCalcLoading(true);
    try {
      const { data } = await api.post("/b2b/calculate-price", {
        pickup_city: form.pickup_city,
        delivery_city: form.delivery_city,
        weight_kg: form.weight_kg || 3,
        monthly_shipments: stats?.monthly_shipments || 0,
        declared_value: form.declared_value || 0,
        item_count: form.item_count || 1,
        addons: form.addons,
      });
      setPricePreview(data);
    } catch {
      toast.error("Failed to calculate price");
    }
    setCalcLoading(false);
  };

  const handleConfirmCod = async (trackingId) => {
    try {
      await api.post(`/orders/${trackingId}/confirm-cod-payment`);
      toast.success("COD payment confirmed!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm");
    }
  };

  const handleRequestReturn = async () => {
    if (!returnReason) { toast.error("Please select a reason"); return; }
    setReturnSubmitting(true);
    try {
      const { data } = await api.post("/b2b/request-return", {
        tracking_id: returnDialog,
        reason: returnReason,
        notes: returnNotes,
      });
      toast.success(`Return ${data.tracking_id} created! Fee: INR ${data.price?.total}`);
      setReturnDialog(null);
      setReturnReason("");
      setReturnNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request return");
    }
    setReturnSubmitting(false);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById("razorpay-b2b-script")) return resolve(true);
      const script = document.createElement("script");
      script.id = "razorpay-b2b-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBook = async () => {
    if (!form.pickup_address || !form.pickup_city || !form.delivery_address || !form.delivery_city || !form.package_description) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!pricePreview) {
      toast.error("Please calculate price first");
      return;
    }
    setBooking(true);
    try {
      const { data } = await api.post("/b2b/create-order", {
        ...form,
        monthly_shipments: stats?.monthly_shipments || 0,
      });

      if (data.razorpay) {
        // Online payment — open Razorpay checkout
        const loaded = await loadRazorpayScript();
        if (!loaded) { toast.error("Failed to load payment gateway"); setBooking(false); return; }

        const options = {
          key: data.razorpay.key_id,
          amount: data.razorpay.amount,
          currency: data.razorpay.currency,
          name: "PickNGo B2B Express",
          description: `B2B Shipment ${data.tracking_id}`,
          order_id: data.razorpay.order_id,
          handler: async (response) => {
            try {
              await api.post("/b2b/verify-payment", {
                tracking_id: data.tracking_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              toast.success(`Payment successful! Order ${data.tracking_id} confirmed.`);
              setShowBook(false);
              setForm({ pickup_address: "", pickup_city: "", delivery_address: "", delivery_city: "", weight_kg: 3, package_description: "", declared_value: 0, payment_method: "online", notes: "", addons: [], item_count: 1 });
              setPricePreview(null);
              fetchData();
            } catch {
              toast.error("Payment verification failed. Contact support.");
            }
          },
          prefill: { email: "", contact: "" },
          theme: { color: "#FF6B00" },
        };
        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); });
        rzp.open();
      } else {
        // COD — order created directly
        toast.success(`B2B order ${data.tracking_id} created!`);
        setShowBook(false);
        setForm({ pickup_address: "", pickup_city: "", delivery_address: "", delivery_city: "", weight_kg: 3, package_description: "", declared_value: 0, payment_method: "online", notes: "", addons: [] });
        setPricePreview(null);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create order");
    }
    setBooking(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" /></div>;

  const addonOptions = [
    { key: "express_packing", label: "Express Packing", price: "₹45", icon: Package },
    { key: "insurance", label: "Insurance (0.8%)", price: "0.8%", icon: ShieldCheck },
    { key: "weekend_pickup", label: "Weekend Pickup", price: "+₹30", icon: Clock },
    { key: "cod", label: "COD (1.5%)", price: "1.5%", icon: IndianRupee },
  ];

  return (
    <div className="space-y-6" data-testid="b2b-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">B2B Express</h1>
          <p className="text-sm text-slate-500 mt-1">Book and manage B2B shipments with volume pricing</p>
        </div>
        <Button onClick={() => setShowBook(!showBook)} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl gap-2 h-11" data-testid="new-b2b-order-btn">
          {showBook ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showBook ? "Close" : "New B2B Shipment"}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
        {[
          { label: "Total Orders", value: stats?.total_orders ?? 0, icon: Package, color: "text-[#FF6B00]", bg: "bg-orange-50" },
          { label: "Delivered", value: stats?.delivered ?? 0, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Active", value: stats?.active ?? 0, icon: Truck, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "This Month", value: stats?.monthly_shipments ?? 0, icon: BarChart3, color: "text-violet-600", bg: "bg-violet-50" },
          { label: "Total Spend", value: `₹${(stats?.total_spend ?? 0).toLocaleString()}`, icon: IndianRupee, color: "text-green-600", bg: "bg-green-50" },
        ].map((c, i) => (
          <div key={c.label} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 card-hover animate-fadeInUp" style={{ animationDelay: `${i * 0.05}s` }}>
            <div className={`w-10 h-10 ${c.bg} rounded-xl flex items-center justify-center mb-3`}>
              <c.icon className={`h-5 w-5 ${c.color}`} />
            </div>
            <p className="font-['Outfit'] text-xl font-bold text-slate-900">{c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Volume Slab Info */}
      {stats?.current_slab && (
        <div className="bg-gradient-to-r from-violet-50 to-blue-50 rounded-2xl border border-violet-200/60 p-4 flex items-center justify-between animate-fadeInUp" data-testid="volume-slab-info">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-500 rounded-xl flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-violet-800">Current Slab: {stats.current_slab}</p>
              <p className="text-xs text-violet-600">{stats.monthly_shipments} shipments this month — ship more to unlock lower rates!</p>
            </div>
          </div>
        </div>
      )}

      {/* Book New Shipment Form */}
      {showBook && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 md:p-6 animate-fadeInUp" data-testid="b2b-booking-form">
          <h2 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-5">Book B2B Shipment</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {/* Left: Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Pickup City *</Label>
                  <Input placeholder="e.g., Mumbai" value={form.pickup_city} onChange={(e) => { setForm({ ...form, pickup_city: e.target.value }); setPricePreview(null); }} className="rounded-xl mt-1 h-10" data-testid="b2b-pickup-city" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Delivery City *</Label>
                  <Input placeholder="e.g., Jaipur" value={form.delivery_city} onChange={(e) => { setForm({ ...form, delivery_city: e.target.value }); setPricePreview(null); }} className="rounded-xl mt-1 h-10" data-testid="b2b-delivery-city" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Pickup Address *</Label>
                <Input placeholder="Full pickup address" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} className="rounded-xl mt-1 h-10" data-testid="b2b-pickup-address" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Delivery Address *</Label>
                <Input placeholder="Full delivery address" value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="rounded-xl mt-1 h-10" data-testid="b2b-delivery-address" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Package Description *</Label>
                  <Input placeholder="e.g., Textile samples" value={form.package_description} onChange={(e) => setForm({ ...form, package_description: e.target.value })} className="rounded-xl mt-1 h-10" data-testid="b2b-description" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">No. of Items</Label>
                  <Input type="number" min="1" step="1" value={form.item_count} onChange={(e) => { setForm({ ...form, item_count: parseInt(e.target.value) || 1 }); setPricePreview(null); }} className="rounded-xl mt-1 h-10" data-testid="b2b-items" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Weight/Item (kg)</Label>
                  <Input type="number" min="0.5" step="0.5" value={form.weight_kg} onChange={(e) => { setForm({ ...form, weight_kg: parseFloat(e.target.value) || 3 }); setPricePreview(null); }} className="rounded-xl mt-1 h-10" data-testid="b2b-weight" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Declared Value (₹)</Label>
                  <Input type="number" min="0" value={form.declared_value} onChange={(e) => { setForm({ ...form, declared_value: parseFloat(e.target.value) || 0 }); setPricePreview(null); }} className="rounded-xl mt-1 h-10" data-testid="b2b-value" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Payment</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {["online", "cod"].map((m) => (
                      <button key={m} onClick={() => setForm({ ...form, payment_method: m })} className={`py-2 rounded-xl text-xs font-semibold border-2 transition-all ${form.payment_method === m ? "border-[#FF6B00] bg-orange-50 text-[#FF6B00]" : "border-slate-200 text-slate-500"}`} data-testid={`b2b-pay-${m}`}>
                        {m === "online" ? "Online" : "COD"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {/* Add-ons */}
              <div>
                <Label className="text-xs font-semibold text-slate-600 mb-2 block">Add-ons</Label>
                <div className="grid grid-cols-2 gap-2">
                  {addonOptions.map((a) => (
                    <button key={a.key} onClick={() => toggleAddon(a.key)} className={`flex items-center gap-2 p-2.5 rounded-xl border-2 text-left transition-all ${form.addons.includes(a.key) ? "border-[#FF6B00] bg-orange-50" : "border-slate-200"}`} data-testid={`b2b-addon-${a.key}`}>
                      <a.icon className={`h-4 w-4 shrink-0 ${form.addons.includes(a.key) ? "text-[#FF6B00]" : "text-slate-400"}`} />
                      <div>
                        <p className={`text-xs font-semibold ${form.addons.includes(a.key) ? "text-[#FF6B00]" : "text-slate-600"}`}>{a.label}</p>
                        <p className="text-[10px] text-slate-400">{a.price}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={calculatePrice} disabled={calcLoading} variant="outline" className="w-full rounded-xl h-10 text-sm gap-2" data-testid="b2b-calc-btn">
                {calcLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <IndianRupee className="h-4 w-4" />}
                Calculate Price
              </Button>
            </div>

            {/* Right: Price Preview */}
            <div>
              {pricePreview ? (
                <div className="bg-gradient-to-br from-orange-50 via-amber-50/50 to-white border border-orange-200/80 rounded-2xl p-5 space-y-4" data-testid="b2b-price-preview">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">Price Breakdown</h3>
                    <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-lg">{pricePreview.tier_label}</span>
                  </div>

                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-emerald-800">SLA: {pricePreview.sla_days}-Day Delivery</p>
                      <p className="text-[10px] text-emerald-600">₹{pricePreview.sla_penalty_guarantee} credit if missed</p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-500">
                      <span>Base charge</span>
                      <span className="font-medium text-slate-700">₹{pricePreview.base_charge}</span>
                    </div>
                    <div className="flex justify-between text-slate-500">
                      <span>Per-kg ({pricePreview.weight_kg}kg × ₹{pricePreview.per_kg_rate})</span>
                      <span className="font-medium text-slate-700">₹{(pricePreview.per_kg_rate * pricePreview.weight_kg).toFixed(0)}</span>
                    </div>
                    {pricePreview.item_count > 1 && (
                      <div className="flex justify-between text-[#FF6B00] text-xs font-semibold">
                        <span>× {pricePreview.item_count} items</span>
                        <span>₹{pricePreview.per_item_price} each</span>
                      </div>
                    )}
                    {pricePreview.volume_slab && (
                      <div className="flex justify-between text-violet-600 text-xs">
                        <span>Volume: {pricePreview.volume_slab}</span>
                        <span className="font-bold">₹{pricePreview.base_price}</span>
                      </div>
                    )}
                    {pricePreview.addons?.map((a) => (
                      <div key={a.key} className="flex justify-between text-slate-500">
                        <span>{a.label}</span>
                        <span className="font-medium text-slate-700">₹{a.cost}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-slate-500">
                      <span>GST (18%)</span>
                      <span className="font-medium text-slate-700">₹{pricePreview.gst}</span>
                    </div>
                    <div className="border-t border-orange-200/60 pt-3 flex justify-between items-center">
                      <span className="font-bold text-slate-900">TOTAL</span>
                      <span className="font-extrabold text-[#FF6B00] text-xl">₹{pricePreview.total}</span>
                    </div>
                  </div>

                  {/* Cost Distribution — Admin Only */}
                  {isAdmin && pricePreview.cost_split && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mt-1 space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Cost Distribution (Admin)</p>
                      {Object.entries(pricePreview.cost_split.per_item || {}).map(([key, c]) => (
                        <div key={key} className="flex justify-between text-xs text-slate-500">
                          <span>{c.label}</span>
                          <span>₹{c.amount} × {pricePreview.item_count}</span>
                        </div>
                      ))}
                      <div className="border-t border-slate-200 pt-1.5 flex justify-between text-xs">
                        <span className="font-semibold text-slate-600">Ops Cost</span>
                        <span className="font-bold text-red-600">-₹{pricePreview.cost_split.total_ops_cost}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="font-semibold text-slate-600">Revenue (excl. GST)</span>
                        <span className="font-bold text-slate-700">₹{pricePreview.cost_split.revenue_before_gst}</span>
                      </div>
                      <div className={`flex justify-between text-xs font-bold ${pricePreview.cost_split.platform_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        <span>Your Profit</span>
                        <span>₹{pricePreview.cost_split.platform_profit} ({pricePreview.cost_split.margin_pct}%)</span>
                      </div>
                    </div>
                  )}

                  <Button onClick={handleBook} disabled={booking} className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-11 text-sm font-semibold gap-2" data-testid="b2b-book-btn">
                    {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                    {booking ? "Booking..." : "Book Shipment"}
                  </Button>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-2xl border border-slate-200 p-8 text-center h-full flex flex-col items-center justify-center">
                  <IndianRupee className="h-10 w-10 text-slate-300 mb-3" />
                  <p className="text-sm font-semibold text-slate-600">Enter cities & weight</p>
                  <p className="text-xs text-slate-400 mt-1">Click "Calculate Price" to see your rate</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Orders & Returns */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="b2b-orders-table">
        <div className="p-5 border-b border-slate-100 flex items-center gap-4">
          <button onClick={() => setActiveTab("orders")} className={`font-['Outfit'] text-base font-semibold pb-1 border-b-2 transition-all ${activeTab === "orders" ? "text-slate-900 border-[#FF6B00]" : "text-slate-400 border-transparent hover:text-slate-600"}`} data-testid="tab-orders">
            B2B Orders <span className="text-xs ml-1 text-slate-400">({orders.length})</span>
          </button>
          <button onClick={() => setActiveTab("returns")} className={`font-['Outfit'] text-base font-semibold pb-1 border-b-2 transition-all ${activeTab === "returns" ? "text-slate-900 border-red-500" : "text-slate-400 border-transparent hover:text-slate-600"}`} data-testid="tab-returns">
            Returns <span className="text-xs ml-1 text-slate-400">({returns.length})</span>
          </button>
        </div>
        {activeTab === "orders" && (orders.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No B2B orders yet</p>
            <p className="text-sm text-slate-400 mt-1">Book your first B2B shipment above!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-5">Tracking</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Route</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Package</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Weight</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Price</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">SLA</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pipeline</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Payment</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((o) => (
                  <TableRow key={o.tracking_id} className="border-slate-50">
                    <TableCell className="pl-5">
                      <span className="text-sm font-mono font-semibold text-slate-900">{o.tracking_id}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <p className="text-slate-700 font-medium">{o.pickup_city}</p>
                        <p className="text-slate-400">→ {o.delivery_city}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{o.package_description}</TableCell>
                    <TableCell className="text-xs text-slate-600">{o.package_weight}kg</TableCell>
                    <TableCell>
                      <span className="text-sm font-bold text-slate-900">₹{o.price?.total?.toLocaleString()}</span>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                        <Clock className="h-2.5 w-2.5" /> {o.sla_days || o.price?.sla_days || "—"}d
                      </span>
                    </TableCell>
                    {/* 6-Stage Pipeline */}
                    <TableCell>
                      <B2BPipeline status={o.status} />
                    </TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border ${
                          o.payment_method === "cod" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-blue-50 text-blue-700 border-blue-200"
                        }`}>
                          {o.payment_method === "cod" ? "COD" : "Online"}
                        </span>
                        {o.payment_status === "paid" ? (
                          <span className="block text-[9px] text-emerald-600 font-semibold">Paid</span>
                        ) : o.payment_method === "cod" && isAdmin ? (
                          <Button size="sm" onClick={() => handleConfirmCod(o.tracking_id)} className="h-6 px-2 text-[9px] rounded-md bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`b2b-confirm-cod-${o.tracking_id}`}>
                            Collect COD
                          </Button>
                        ) : o.status === "pending_payment" ? (
                          <span className="block text-[9px] text-amber-600 font-semibold">Awaiting Payment</span>
                        ) : (
                          <span className="block text-[9px] text-amber-600 font-semibold">Pending</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {["picked_up", "packing", "at_origin_hub", "in_linehaul", "at_dest_hub", "out_for_delivery", "delivered"].includes(o.status) && !o.has_return ? (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setReturnDialog(o.tracking_id); }} className="h-7 px-2 text-[10px] rounded-lg border-red-200 text-red-600 hover:bg-red-50 gap-1" data-testid={`return-btn-${o.tracking_id}`}>
                            <RotateCcw className="h-3 w-3" /> Return
                          </Button>
                        ) : o.has_return ? (
                          <span className="text-[9px] text-amber-600 font-semibold">{o.return_tracking_id}</span>
                        ) : null}
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/invoice/${o.tracking_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-[#FF6B00] hover:border-[#FF6B00]/30 font-semibold"
                          data-testid={`b2b-invoice-btn-${o.tracking_id}`}
                          title="Download Invoice"
                        >
                          <Download className="h-3 w-3" /> Invoice
                        </a>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}

        {/* Returns Tab */}
        {activeTab === "returns" && (returns.length === 0 ? (
          <div className="p-12 text-center">
            <RotateCcw className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No returns yet</p>
            <p className="text-sm text-slate-400 mt-1">You can request returns on picked up or delivered orders</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100 bg-slate-50/60">
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400 pl-5">Return ID</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Original</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Route</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Reason</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Fee</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Pipeline</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Status</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Date</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Invoice</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returns.map((r) => (
                  <TableRow key={r.tracking_id} className="border-slate-50">
                    <TableCell className="pl-5">
                      <span className="text-sm font-mono font-semibold text-red-600">{r.tracking_id}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-slate-500">{r.original_tracking_id}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-xs">
                        <p className="text-slate-700 font-medium">{r.pickup_city}</p>
                        <p className="text-slate-400">→ {r.delivery_city}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-red-600 font-semibold">{r.return_reason}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-bold text-slate-900">₹{r.price?.total}</span>
                    </TableCell>
                    <TableCell><ReversePipeline status={r.status} /></TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/invoice/${r.tracking_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 h-7 px-2 text-[10px] rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-[#FF6B00] hover:border-[#FF6B00]/30 font-semibold"
                        data-testid={`b2b-return-invoice-btn-${r.tracking_id}`}
                      >
                        <Download className="h-3 w-3" /> PDF
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
      </div>

      {/* Return Request Dialog */}
      {returnDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setReturnDialog(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="return-dialog">
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-900 mb-1">Request Return</h3>
            <p className="text-sm text-slate-500 mb-5">Order: <span className="font-mono font-semibold">{returnDialog}</span></p>

            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Reason *</Label>
                <select value={returnReason} onChange={(e) => setReturnReason(e.target.value)} className="w-full mt-1 rounded-xl h-11 border border-slate-200 px-3 text-sm focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/20" data-testid="return-reason-select">
                  <option value="">Select reason</option>
                  {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Notes (optional)</Label>
                <textarea value={returnNotes} onChange={(e) => setReturnNotes(e.target.value)} placeholder="Additional details..." className="w-full mt-1 rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none h-20 focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/20" data-testid="return-notes" />
              </div>
              <p className="text-xs text-slate-400">Reverse logistics fee will be charged (₹75 + GST)</p>
              <div className="flex gap-2">
                <Button onClick={() => setReturnDialog(null)} variant="outline" className="flex-1 rounded-xl">Cancel</Button>
                <Button onClick={handleRequestReturn} disabled={returnSubmitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl gap-1" data-testid="return-submit-btn">
                  {returnSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Confirm Return
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
